/**
 * requiredIntakeFields — THE ONE PLACE a field is declared required.
 *
 * Card CONTACT-PROFILE-INTAKE item 9 (Carlos, CRM 2.0 meeting 9 Oct 2026).
 *
 * ⛔⛔ THE LIST SHIPPED HERE IS NOT A PROPOSAL. It is the set the DATABASE
 * already refuses to store a contact without, read out of
 * `database/schema.sql:464-490` on 2026-10-10:
 *
 *     contact_type TEXT NOT NULL CHECK(contact_type IN (...))
 *     first_name   TEXT NOT NULL
 *     last_name    TEXT NOT NULL
 *
 * Nothing else on `crm_contacts` is NOT NULL that a human fills in —
 * `is_active`, `created_by`, `created_at` and `updated_at` are set by the
 * server and the schema default. Migrations 035-174 add columns and CHECK
 * vocabularies; not one adds a NOT NULL.
 *
 * ⚠ `referral_answer` is DELIBERATELY ABSENT and that is a judgement, not an
 * oversight. It is required at CREATION by the mig-161 trigger
 * `trg_crm_contacts_referral_answer_required` — which is `BEFORE INSERT` only.
 * The column itself is nullable and no constraint touches an UPDATE, so on an
 * EDIT surface the database does not require it. Rows created before migration
 * 160 legitimately hold NULL. Flagging it here would light up every legacy
 * contact with something the rep cannot be blamed for and the DB will not
 * reject. It is in the report for Daniel and Carlos to rule on.
 *
 * ⛔ WHAT THIS LIST MUST NEVER DO IS BLOCK A SAVE. Carlos's item 9 says
 * "highlighted … not blocking saves", and the reason is the room the form is
 * filled in: a PEM is on the phone with a patient and types what they are
 * told, in the order they are told it. A validator that refuses to save until
 * the shape is complete loses the half that was true.
 *
 * ⚠⚠ NOT NULL IS NOT NOT-BLANK, AND THE DIFFERENCE IS LOAD-BEARING. Measured
 * against a real Postgres in tests/integration/contactIntakeFullRowUpdate:
 * saving `first_name = '   '` SUCCEEDS. The constraint rejects NULL and
 * nothing else. So the database decides WHICH fields are required and this
 * file's `isAnswered` decides what counts as an answer — two different claims,
 * both needed. A highlight built on plain truthiness goes out the moment a rep
 * types a space, and the record is blank in every report thereafter.
 *
 * ⇒ To add a field: add a row to REQUIRED_INTAKE_FIELDS. The intake section's
 * highlight, its missing-count, and the tests all read THIS array; there is no
 * second list to keep in step.
 */

/**
 * @typedef {Object} RequiredIntakeField
 * @property {string} name   Column name on `crm_contacts`.
 * @property {string} label  What the rep sees, matching the intake field label.
 * @property {string} source Why it is required. Audit trail for the next reader.
 */

/** @type {RequiredIntakeField[]} */
export const REQUIRED_INTAKE_FIELDS = [
  { name: 'first_name',   label: 'First name',   source: 'crm_contacts.first_name NOT NULL' },
  { name: 'last_name',    label: 'Last name',    source: 'crm_contacts.last_name NOT NULL' },
  { name: 'contact_type', label: 'Contact type', source: 'crm_contacts.contact_type NOT NULL' },
];

/**
 * Is this value present enough to count as answered?
 *
 * ⚠ A STRING OF SPACES IS NOT AN ANSWER. `' '` is truthy in JavaScript and
 * would silently clear the highlight while the database stores whitespace —
 * the field would read as done on screen and be empty in every report.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isAnswered(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/**
 * Which required fields this contact is still missing.
 *
 * @param {Record<string, unknown>|null|undefined} contact
 * @returns {RequiredIntakeField[]} in REQUIRED_INTAKE_FIELDS order
 */
export function missingRequiredFields(contact) {
  const c = contact || {};
  return REQUIRED_INTAKE_FIELDS.filter((f) => !isAnswered(c[f.name]));
}

/**
 * The sentence shown on the intake section, or '' when nothing is missing.
 *
 * ⚠ Returns '' rather than "0 required fields missing": a count of zero is a
 * banner that says nothing is wrong, which is still a banner.
 *
 * @param {Record<string, unknown>|null|undefined} contact
 * @returns {string}
 */
export function missingRequiredSummary(contact) {
  const n = missingRequiredFields(contact).length;
  if (n === 0) return '';
  return `${n} required field${n === 1 ? '' : 's'} missing`;
}
